/**
 * Shape dos campos editáveis pela UI Superadmin em
 * `/superadmin/settings/platform`. Cada campo mapeia pra uma row em
 * `public.platform_settings` (key → value). A tabela é genérica
 * (key/jsonb), mas a UI aqui lista um conjunto fixo e tipado de 8
 * campos — os que a Karla/Alexandre pediram editáveis sem deploy
 * (dados da contratada + footer institucional das propostas).
 *
 * Quando adicionar um campo novo aqui: atualize também
 * `apps/web/src/features/proposals/platform-settings.ts` pra puxar
 * no read (se for pra aparecer na proposta pública), e inclua num
 * seed inicial em migration nova caso ambiente novo precise de um
 * valor default. */
import { z } from 'zod';

/** Cada campo editável — key canônica da tabela + metadata pra UI. */
export interface PlatformSettingField {
  /** Chave exata em `platform_settings.key` (ex: `contratada.cnpj`). */
  key: string;
  /** Label curto exibido como nome do campo. */
  label: string;
  /** Dica explicando quando/onde esse valor aparece na proposta. */
  helpText: string;
  /** Grupo lógico — usado pra seccionar o form. */
  group: 'contratada' | 'footer' | 'proposal_copy' | 'contract';
  /** Tipo de input. `text` = input single-line; `textarea` = multi-line. */
  input: 'text' | 'textarea';
  /** Valor obrigatório? Bloqueia salvar quando vazio. */
  required: boolean;
  /** Placeholder exibido quando vazio. */
  placeholder?: string;
}

/** Ordem de definição aqui = ordem de exibição no formulário. */
export const PLATFORM_SETTING_FIELDS: readonly PlatformSettingField[] = [
  // ── Contratada (dados legais/comerciais da KM Gestão) ──
  {
    key: 'contratada.razao_social',
    label: 'Razão social',
    helpText: 'Nome legal da empresa. Aparece no rodapé da proposta pública.',
    group: 'contratada',
    input: 'text',
    required: true,
    placeholder: 'Ex.: KM Serviços Administrativos LTDA',
  },
  {
    key: 'contratada.cnpj',
    label: 'CNPJ',
    helpText: 'CNPJ exibido no rodapé da proposta e usado em contratos.',
    group: 'contratada',
    input: 'text',
    required: true,
    placeholder: '00.000.000/0000-00',
  },
  {
    key: 'contratada.nome_fantasia',
    label: 'Nome fantasia',
    helpText: 'Nome comercial. Aparece no header e em emails.',
    group: 'contratada',
    input: 'text',
    required: true,
    placeholder: 'Ex.: KM Gestão',
  },
  {
    key: 'contratada.cidade_uf',
    label: 'Cidade / UF',
    helpText: 'Localidade da sede. Rodapé da proposta.',
    group: 'contratada',
    input: 'text',
    required: true,
    placeholder: 'Ex.: Divinópolis/MG',
  },
  {
    key: 'contratada.email_contato',
    label: 'Email de contato',
    helpText: 'Email institucional — mostrado como link no rodapé.',
    group: 'contratada',
    input: 'text',
    required: true,
    placeholder: 'contato@kmgestao.com.br',
  },
  {
    key: 'contratada.responsavel_nome',
    label: 'Responsável (nome de exibição)',
    helpText:
      'Nome curto que aparece na proposta, emails e UI ("Preparada por…", rodapé, assinatura). Ex.: "Karla Martins".',
    group: 'contratada',
    input: 'text',
    required: true,
    placeholder: 'Ex.: Karla Martins',
  },
  {
    key: 'contratada.responsavel_nome_legal',
    label: 'Responsável (nome jurídico completo)',
    helpText:
      'Nome jurídico completo usado APENAS no contrato PDF (cabeçalho "neste ato representada por…"). Em branco, cai pro nome de exibição. Ex.: "Karla das Dores Silva".',
    group: 'contratada',
    input: 'text',
    required: false,
    placeholder: 'Ex.: Karla das Dores Silva',
  },
  {
    key: 'contratada.responsavel_cargo',
    label: 'Responsável (cargo)',
    helpText: 'Cargo/credenciais do responsável — aparece logo abaixo do nome no rodapé.',
    group: 'contratada',
    input: 'text',
    required: true,
    placeholder: 'Ex.: Administradora de Empresas · Especialista em Gestão de Clínicas',
  },
  {
    key: 'contratada.endereco',
    label: 'Endereço completo',
    helpText:
      'Rua, número, complemento e bairro da sede. Aparece no cabeçalho "Partes" do contrato em PDF. Obrigatório antes do primeiro contrato em produção.',
    group: 'contratada',
    input: 'text',
    required: false,
    placeholder: 'Ex.: Rua das Palmeiras, 500, Sala 301, Centro',
  },
  {
    key: 'contratada.cep',
    label: 'CEP',
    helpText: 'CEP da sede, no formato XXXXX-XXX. Aparece ao lado do endereço no contrato.',
    group: 'contratada',
    input: 'text',
    required: false,
    placeholder: 'Ex.: 35500-000',
  },
  // ── Footer institucional (texto legal curto que fecha as propostas) ──
  {
    key: 'proposal.footer_institutional',
    label: 'Texto institucional do rodapé',
    helpText:
      'Frase de fechamento que aparece no final de TODAS as propostas públicas (ex: compromisso de sigilo). Texto longo; use quebras de linha se precisar.',
    group: 'footer',
    input: 'textarea',
    required: true,
    placeholder: 'A proponente se compromete a guardar em sigilo todas as informações levantadas…',
  },
  // ── Textos do corpo da proposta (bloco 01 hero, bloco 02 "Sobre nós",
  //    bullets de Considerações). Migration 0053. Tokens disponíveis:
  //      {firstName}, {destinatario}, {razao_social}, {nome_fantasia}
  {
    key: 'proposal.hero_title',
    label: 'Bloco 01 — Título',
    helpText:
      'Título do primeiro bloco da proposta (abertura). Aceita placeholders {firstName} (primeiro nome do contato) e {destinatario} (clínica / Dr.).',
    group: 'proposal_copy',
    input: 'text',
    required: true,
    placeholder: 'Ex.: Olá {firstName}, é um prazer',
  },
  {
    key: 'proposal.hero_intro_template',
    label: 'Bloco 01 — Parágrafo de abertura',
    helpText:
      'Parágrafo único do bloco 01. Placeholders: {firstName} (nome do contato) e {destinatario} (clínica / Dr.).',
    group: 'proposal_copy',
    input: 'textarea',
    required: true,
    placeholder: '{firstName}, é com imensa satisfação que apresentamos nossa proposta…',
  },
  {
    key: 'proposal.about_us_title',
    label: 'Bloco 02 — Título',
    helpText:
      'Título do bloco "Sobre nós". Aceita placeholders {razao_social} e {nome_fantasia} se quiser personalizar.',
    group: 'proposal_copy',
    input: 'text',
    required: true,
    placeholder: 'Ex.: Sobre nós',
  },
  {
    key: 'proposal.about_us_paragraphs',
    label: 'Bloco 02 — Parágrafos institucionais',
    helpText:
      'Separe cada parágrafo com UMA linha em branco (duas quebras). Placeholders: {razao_social}, {nome_fantasia}.',
    group: 'proposal_copy',
    input: 'textarea',
    required: true,
    placeholder: 'Com mais de 15 anos de experiência, {razao_social}…\n\nCombinamos estratégias…',
  },
  {
    key: 'proposal.considerations_bullets',
    label: 'Considerações — Bullets estáticos',
    helpText:
      'Termos legais padrão do bloco "Considerações". UMA linha por bullet. Os bullets DINÂMICOS (validade da proposta e footer institucional) continuam automáticos.',
    group: 'proposal_copy',
    input: 'textarea',
    required: true,
    placeholder:
      'Ao aceitar, a contratante se compromete…\nA contratada se compromete a desenvolver…',
  },
  {
    key: 'proposal.post_consulting_terms_template',
    label: 'Considerações — Bullet pós-consultoria (só propostas com consultoria)',
    helpText:
      'Bullet que aparece APENAS em propostas que incluem consultoria (Só Consultoria ou Combo). Placeholders: {consulting_duration_months} (número de meses) e {saas_cobranca_note} (trecho sobre cobrança — preenchido automaticamente em Combo, vazio em Só Consultoria).',
    group: 'proposal_copy',
    input: 'textarea',
    required: true,
    placeholder:
      'Ao fim do período contratado de consultoria ({consulting_duration_months} meses), a contratante será consultada sobre a continuidade…',
  },
  // ── Contrato — Cláusula 1 (Do Objeto) — 3 variantes por proposal_type ──
  // Placeholders disponíveis: {razao_social}, {nome_fantasia}, {cnpj},
  // {endereco}, {cep}, {cidade_uf}, {email_contato}, {responsavel_nome},
  // {responsavel_cargo}, {lead_name}, {clinic_name}, {lead_cnpj},
  // {lead_email}, {lead_phone}, {plan_name}, {monthly_price},
  // {yearly_price}, {billing_cycle}, {trial_days},
  // {consulting_duration_months}, {consulting_total_price},
  // {consulting_scope_notes}, {signing_date}.
  {
    key: 'contract.objeto_saas_body',
    label: 'Cláusula 1 — Objeto (só SaaS)',
    helpText:
      'Texto da cláusula 1 quando a proposta é apenas de licença de software (sem consultoria). Use {plan_name} e {nome_fantasia}.',
    group: 'contract',
    input: 'textarea',
    required: true,
    placeholder: 'A CONTRATADA concede à CONTRATANTE licença de uso do sistema {nome_fantasia}…',
  },
  {
    key: 'contract.objeto_consulting_body',
    label: 'Cláusula 1 — Objeto (só Consultoria)',
    helpText:
      'Texto da cláusula 1 quando a proposta é apenas de consultoria (sem SaaS). Use {consulting_duration_months} e {consulting_scope_notes}.',
    group: 'contract',
    input: 'textarea',
    required: true,
    placeholder:
      'A CONTRATADA prestará à CONTRATANTE serviços de consultoria por {consulting_duration_months} meses…',
  },
  {
    key: 'contract.objeto_saas_consulting_body',
    label: 'Cláusula 1 — Objeto (Combo SaaS + Consultoria)',
    helpText:
      'Texto da cláusula 1 quando a proposta inclui os dois serviços. Deve mencionar licença ({plan_name}) e consultoria ({consulting_duration_months} meses).',
    group: 'contract',
    input: 'textarea',
    required: true,
    placeholder: 'O presente contrato tem por objeto DOIS serviços complementares: (a) Licença…',
  },
  // ── Contrato — Cláusula 2 (Do Valor) — 3 variantes por proposal_type ──
  {
    key: 'contract.valor_saas_body',
    label: 'Cláusula 2 — Valor (só SaaS)',
    helpText: 'Valor mensal da licença. Use {monthly_price} e {billing_cycle}.',
    group: 'contract',
    input: 'textarea',
    required: true,
    placeholder:
      'O valor mensal do plano contratado é de {monthly_price}, com cobrança {billing_cycle}…',
  },
  {
    key: 'contract.valor_consulting_body',
    label: 'Cláusula 2 — Valor (só Consultoria)',
    helpText:
      'Valor total da consultoria. Use {consulting_total_price} e {consulting_duration_months}.',
    group: 'contract',
    input: 'textarea',
    required: true,
    placeholder:
      'O valor total da consultoria é de {consulting_total_price}, referente aos {consulting_duration_months} meses…',
  },
  {
    key: 'contract.valor_saas_consulting_body',
    label: 'Cláusula 2 — Valor (Combo)',
    helpText:
      'Valor combinado (SaaS mensal + Consultoria total). Deve mencionar {monthly_price} e {consulting_total_price}.',
    group: 'contract',
    input: 'textarea',
    required: true,
    placeholder:
      'O valor deste contrato é composto por DUAS parcelas: (a) Licença ({plan_name}): {monthly_price}/mês…',
  },
  // ── Cláusulas universais (mesmo texto nos 3 tipos de proposta) ──
  {
    key: 'contract.prazo_body',
    label: 'Cláusula 3 — Prazo e Renovação',
    helpText:
      'Prazo inicial e renovação. Use {signing_date} e {consulting_duration_months}. Trata SaaS (renovação) e Consultoria (prazo finito) juntos.',
    group: 'contract',
    input: 'textarea',
    required: true,
    placeholder:
      'O presente contrato entra em vigor em {signing_date} e vigorará por 12 meses, renovando-se…',
  },
  {
    key: 'contract.rescisao_body',
    label: 'Cláusula 4 — Rescisão',
    helpText: 'Hipóteses de rescisão com e sem multa. Sem placeholders (texto universal).',
    group: 'contract',
    input: 'textarea',
    required: true,
    placeholder: 'O contrato pode ser rescindido por qualquer das partes, sem multa, mediante…',
  },
  {
    key: 'contract.lgpd_body',
    label: 'Cláusula 5 — Proteção de Dados (LGPD)',
    helpText: 'Compromissos LGPD. Placeholder: {email_contato} (política de privacidade).',
    group: 'contract',
    input: 'textarea',
    required: true,
    placeholder: 'As partes comprometem-se com o pleno cumprimento da Lei 13.709/2018 (LGPD)…',
  },
  {
    key: 'contract.confidencialidade_body',
    label: 'Cláusula 6 — Confidencialidade',
    helpText: 'Sigilo por X anos pós-rescisão (hoje 5 anos). Sem placeholders.',
    group: 'contract',
    input: 'textarea',
    required: true,
    placeholder:
      'Todas as informações trocadas entre as partes em razão deste contrato são confidenciais…',
  },
  {
    key: 'contract.foro_body',
    label: 'Cláusula 7 — Foro',
    helpText:
      'Foro eleito (hoje Divinópolis/MG) + tentativa de composição amigável. Sem placeholders.',
    group: 'contract',
    input: 'textarea',
    required: true,
    placeholder:
      'Fica eleito o foro da comarca de Divinópolis/MG para dirimir quaisquer controvérsias…',
  },
  {
    key: 'contract.disposicoes_gerais_body',
    label: 'Cláusula 8 — Disposições Gerais',
    helpText:
      'Integralidade, alterações, tolerância, comunicação formal. Use {email_contato} e {lead_email}.',
    group: 'contract',
    input: 'textarea',
    required: true,
    placeholder:
      'Este contrato representa o acordo integral entre as partes. Alterações só têm validade…',
  },
] as const;

/** Schema dinâmico de validação do form — exige todos os `required`
 * não-vazios (após trim). Campos opcionais aceitam string vazia. */
export function buildPlatformSettingsFormSchema() {
  const shape: Record<string, z.ZodTypeAny> = {};
  for (const field of PLATFORM_SETTING_FIELDS) {
    if (field.required) {
      shape[field.key] = z.string().trim().min(1, `${field.label} é obrigatório.`);
    } else {
      shape[field.key] = z.string();
    }
  }
  return z.object(shape);
}

export type PlatformSettingsFormInput = Record<string, string>;
